import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, UserMinusIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { projectAPI } from '../../services/api';
import toast from 'react-hot-toast';

export default function EditProjectModal({ project, onClose }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'planning',
    budget: '',
    start_date: '',
    end_date: '',
    probability_of_success: 50,
  });
  const [selectedMemberId, setSelectedMemberId] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_URL || '';

  // Fetch all team members
  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/team`);
      if (!response.ok) throw new Error('Failed to fetch team members');
      return response.json();
    },
  });

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        description: project.description || '',
        priority: project.priority || 'medium',
        status: project.status || 'planning',
        budget: project.budget || '',
        start_date: project.start_date ? project.start_date.split('T')[0] : '',
        end_date: project.end_date ? project.end_date.split('T')[0] : '',
        probability_of_success: project.probability_of_success || 50,
      });
    }
  }, [project]);

  const updateMutation = useMutation({
    mutationFn: (data) => projectAPI.updateProject(project.project_id, data),
    onSuccess: () => {
      toast.success('Project updated successfully!');
      queryClient.invalidateQueries(['project', project.project_id]);
      queryClient.invalidateQueries(['projects']);
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update project: ${error.message}`);
    },
  });

  const assignMemberMutation = useMutation({
    mutationFn: async (memberId) => {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${project.project_id}/assign-member?member_id=${memberId}`,
        { method: 'POST' }
      );
      if (!response.ok) throw new Error('Failed to assign member');
      return response.json();
    },
    onSuccess: () => {
      toast.success('Team member assigned successfully!');
      queryClient.invalidateQueries(['project', project.project_id]);
      setSelectedMemberId('');
    },
    onError: (error) => {
      toast.error(`Failed to assign member: ${error.message}`);
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId) => {
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${project.project_id}/remove-member/${memberId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error('Failed to remove member');
      return response.json();
    },
    onSuccess: () => {
      toast.success('Team member removed successfully!');
      queryClient.invalidateQueries(['project', project.project_id]);
    },
    onError: (error) => {
      toast.error(`Failed to remove member: ${error.message}`);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare data
    const updateData = {
      ...formData,
      budget: formData.budget ? parseFloat(formData.budget) : null,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
    };

    updateMutation.mutate(updateData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Edit Project</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div className="form-group">
            <label htmlFor="title" className="form-label">
              Project Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              required
              className="form-input"
              value={formData.title}
              onChange={handleChange}
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              className="form-textarea"
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          {/* Status and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                className="form-select"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="priority" className="form-label">
                Priority
              </label>
              <select
                id="priority"
                name="priority"
                className="form-select"
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="form-group">
              <label htmlFor="start_date" className="form-label">
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                className="form-input"
                value={formData.start_date}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="end_date" className="form-label">
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                className="form-input"
                value={formData.end_date}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Budget */}
          <div className="form-group">
            <label htmlFor="budget" className="form-label">
              Budget (£)
            </label>
            <input
              type="number"
              id="budget"
              name="budget"
              min="0"
              step="0.01"
              className="form-input"
              value={formData.budget}
              onChange={handleChange}
              placeholder="0.00"
            />
          </div>

          {/* Success Probability */}
          <div className="form-group">
            <label htmlFor="probability_of_success" className="form-label">
              Success Probability: {formData.probability_of_success}%
            </label>
            <input
              type="range"
              id="probability_of_success"
              name="probability_of_success"
              min="0"
              max="100"
              className="w-full"
              value={formData.probability_of_success}
              onChange={handleChange}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Team Members */}
          <div className="form-group">
            <label className="form-label">Team Members</label>
            
            {/* Current Team Members */}
            <div className="mb-3 space-y-2">
              {project.team_members && project.team_members.length > 0 ? (
                project.team_members.map((member) => (
                  <div
                    key={member.member_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-sm">
                          {member.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{member.name}</p>
                        <p className="text-xs text-gray-500">{member.role}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMemberMutation.mutate(member.member_id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove from project"
                    >
                      <UserMinusIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 py-2">No team members assigned</p>
              )}
            </div>

            {/* Add Team Member */}
            <div className="flex gap-2">
              <select
                value={selectedMemberId}
                onChange={(e) => setSelectedMemberId(e.target.value)}
                className="form-select flex-1"
              >
                <option value="">Select a team member...</option>
                {Array.isArray(teamMembers) &&
                  teamMembers
                    .filter(
                      (member) =>
                        !project.team_members?.some((pm) => pm.member_id === member.member_id)
                    )
                    .map((member) => (
                      <option key={member.member_id} value={member.member_id}>
                        {member.name} - {member.role}
                      </option>
                    ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  if (selectedMemberId) {
                    assignMemberMutation.mutate(selectedMemberId);
                  }
                }}
                disabled={!selectedMemberId || assignMemberMutation.isPending}
                className="btn-secondary flex items-center gap-2 whitespace-nowrap"
              >
                <PlusIcon className="w-4 h-4" />
                Add
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="btn-primary flex-1"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
